'use client';

import { useContext, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import MobileShellContext from '@/components/Mobile/MobileShellContext';
import { PAGE_MOBILE } from '@/lib/constants';
import { setNavState } from '@/lib/utils/navState';
import { SentenceScramble } from '@/lib/utils/SentenceScramble';
import type { ProjectModel } from '@/lib/models/Project';
import styles from './directory-mobile.module.scss';

interface Props {
  projects: ProjectModel[];
}

interface ItemSpec {
  label: string;
  icon: string;
  slide?: number;
  href: string;
}

export default function DirectoryMobile({ projects }: Props) {
  const router = useRouter();
  const { directoryOpen, closeDirectory, requestSlide } = useContext(MobileShellContext);

  // REACH sits in the DOM after TITLE / LOADING / INTRO and all project slides,
  // so its realIndex is the project count plus the leading TITLE / LOADING /
  // INTRO offset (which is PAGE_MOBILE.PROJECTS = 3).
  const reachRealIndex = PAGE_MOBILE.PROJECTS + projects.length;

  const items: ItemSpec[] = [
    { label: 'ABOUT US', icon: '/assets/image/mobile/ABOUT US.svg', slide: PAGE_MOBILE.INTRO, href: '/' },
    { label: 'CAPABILITIES', icon: '/assets/image/mobile/CAPABILITIES.svg', slide: PAGE_MOBILE.LOADING, href: '/' },
    { label: 'OUR WORKS', icon: '/assets/image/mobile/OUR_WORKS.svg', slide: PAGE_MOBILE.PROJECTS, href: '/' },
    // { label: 'LAB NOTES', icon: '/assets/image/mobile/LAB_NOTES.svg', href: '/blog' },
    { label: 'CONTACT', icon: '/assets/image/mobile/CONTACT.svg', slide: reachRealIndex, href: '/' },
  ];

  useEffect(() => {
    if (!directoryOpen) return;
    const scrambler = new SentenceScramble('directory-scrambler', () => {}, true);
    scrambler.startScramble(0.66, 0);
  }, [directoryOpen]);

  if (!directoryOpen) return null;

  const handleItemClick = (item: ItemSpec) => {
    if (typeof item.slide === 'number') {
      // navState carries the slide across full route-group changes (e.g. /blog → /)
      // where the MobileShell remounts and the context is lost.
      setNavState(item.slide);
      // requestSlide carries it for same-route nav (already on /, where router.push
      // is a no-op for pathname so HomeMobile's [pathname] effect doesn't refire).
      requestSlide(item.slide);
    }
    closeDirectory();
    router.push(item.href);
  };

  return (
    <div
      className={styles.modalBackdrop}
      onClick={closeDirectory}
      role='presentation'
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role='dialog'
        aria-modal='true'
        aria-label='Directory menu'
      >
        <div className={styles.modalHeader}>
          <img
            src='/assets/image/mobile/DIRECTORY.svg'
            alt=''
            aria-hidden='true'
            className={styles.modalIcon}
          />
          <h2 className={`${styles.modalTitle} directory-scrambler`}>DIRECTORY</h2>
          <button
            type='button'
            className={styles.modalClose}
            onClick={closeDirectory}
            aria-label='Close directory'
          >
            <img
              src='/assets/image/mobile/WindowClose.svg'
              alt=''
              aria-hidden='true'
            />
          </button>
        </div>
        <ul className={styles.modalList}>
          {items.map((item) => (
            <li key={item.label}>
              <button
                type='button'
                className={styles.modalItem}
                onClick={() => handleItemClick(item)}
              >
                <img
                  src={item.icon}
                  alt=''
                  aria-hidden='true'
                  className={styles.modalItemIcon}
                />
                <span className='directory-scrambler'>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
